// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore mustache is in the parent dir
import Mustache from 'mustache';

import { Maybe, Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';

import { LTLQuote, LTLQuoteLaneAddress, QuoteFormStop } from '../../types';
import { formatLTLDraftLocation, formatLTLQuoteForTemplate } from '../../util';
import { toTitleCase } from '../../utils/formatting';
import {
  BATCH_LTL_RATE_FRACTION_DIGITS,
  BATCH_LTL_SHIPPER_ERROR_TEXT,
} from '../constants';
import {
  BatchLTLMissingField,
  BatchLTLShipmentInput,
  BatchLTLShipmentRun,
  BatchLTLShipmentStatus,
} from '../types';
import { getFreightSummary } from './shipmentDisplay';

export type BatchLTLDraftShipment = {
  shipment: BatchLTLShipmentInput;
  run: Undef<BatchLTLShipmentRun>;
  selectedQuotes: LTLQuote[];
  missingFields: BatchLTLMissingField[];
};

type BatchLTLDraftInput = {
  customerName: Maybe<string>;
  shipments: BatchLTLDraftShipment[];
  // quickQuoteConfig.hideTaiLimitedLiability, as in the single LTL reply.
  hideTaiLimitedLiability: boolean;
};

// Shipper-facing wording for each missing field in the inline question.
const MISSING_FIELD_QUESTION_COPY: Record<BatchLTLMissingField, string> = {
  [BatchLTLMissingField.Origin]: 'pickup ZIP code',
  [BatchLTLMissingField.Destination]: 'delivery ZIP code',
  [BatchLTLMissingField.Items]: 'pallet count, dimensions and weight',
  [BatchLTLMissingField.Description]: 'commodity',
  [BatchLTLMissingField.Pieces]: 'pallet count',
  [BatchLTLMissingField.Weight]: 'weight',
  [BatchLTLMissingField.FreightClass]: 'freight class',
  [BatchLTLMissingField.PickupWindow]: 'pickup date',
  [BatchLTLMissingField.AccessorialDetails]: 'accessorial details',
};

// Renders the batch LTL reply; variable names match the backend template's view model.
export function formatBatchLTLDraftTemplate(
  templateBody: string,
  input: BatchLTLDraftInput
): string {
  return Mustache.render(templateBody, buildBatchLTLDraftView(input));
}

export function buildBatchLTLDraftView({
  customerName,
  shipments,
  hideTaiLimitedLiability,
}: BatchLTLDraftInput) {
  return {
    customerName: customerName ?? '',
    hasCustomer: Boolean(customerName),
    shipmentCount: shipments.length,
    shipments: shipments.map((shipment, index) =>
      buildShipmentView(shipment, index, hideTaiLimitedLiability)
    ),
  };
}

function buildShipmentView(
  { shipment, run, selectedQuotes, missingFields }: BatchLTLDraftShipment,
  index: number,
  hideTaiLimitedLiability: boolean
) {
  const isTaiQuote = run?.externalQuote.source?.toLowerCase() === TMS.Tai;
  // Every per-rate variable the single LTL reply has (service level, guaranteed,
  // coverage...); batch replies use the short transit text shippers write.
  const rates = selectedQuotes
    .map((quote) =>
      formatLTLQuoteForTemplate(quote, {
        rateFractionDigits: BATCH_LTL_RATE_FRACTION_DIGITS,
        hideLimitedLiability: isTaiQuote && hideTaiLimitedLiability,
      })
    )
    .sort((a, b) => a.rateAmount - b.rateAmount)
    .map((quote, rateIndex) => ({
      ...quote,
      transitDaysText: getShortTransitText(quote.transitDays),
      isCheapest: rateIndex === 0,
    }));
  const lane = run?.quotes[0];
  const accessorialsText = (shipment.ltl?.selectedAccessorials ?? [])
    .map((accessorial) => accessorial.name)
    .join(', ');
  const quoteNumber =
    run?.status === BatchLTLShipmentStatus.Rated ? run.externalQuote.id : null;
  const hasMissingInfo = missingFields.length > 0 && !run;

  return {
    index: index + 1,
    label: shipment.label,
    originLabel: getDraftStopLabel(
      shipment.stops[0],
      lane?.pickupLocation,
      'Origin'
    ),
    destinationLabel: getDraftStopLabel(
      shipment.stops[shipment.stops.length - 1],
      lane?.deliveryLocation,
      'Destination'
    ),
    freightSummary: getFreightSummary(
      shipment.ltl?.items ?? [],
      shipment.ltl?.commodity?.freightClass
    ),
    accessorialsText,
    hasAccessorials: Boolean(accessorialsText),
    quoteNumberLabel: isTaiQuote ? 'Tai Quote #' : 'Quote #',
    quoteNumber: quoteNumber ?? '',
    hasQuoteNumber: Boolean(quoteNumber),
    hasRates: rates.length > 0,
    rates,
    hasMissingInfo,
    missingInfoQuestion: hasMissingInfo
      ? getMissingInfoQuestion(missingFields)
      : '',
    hasError: run?.status === BatchLTLShipmentStatus.Failed,
    errorText:
      run?.status === BatchLTLShipmentStatus.Failed
        ? BATCH_LTL_SHIPPER_ERROR_TEXT
        : '',
  };
}

// e.g. "What is the weight and freight class of this shipment?"
export function getMissingInfoQuestion(
  missingFields: BatchLTLMissingField[]
): string {
  const copy = missingFields.map((field) => MISSING_FIELD_QUESTION_COPY[field]);
  const list =
    copy.length > 1
      ? `${copy.slice(0, -1).join(', ')} and ${copy[copy.length - 1]}`
      : copy[0];

  return `What is the ${list} of this shipment?`;
}

// Batch replies use the short form shippers write: "4 days", "1 day".
function getShortTransitText(transitDays: number): string {
  if (!Number.isFinite(transitDays) || transitDays <= 0) {
    return 'N/A';
  }

  return transitDays === 1 ? '1 day' : `${transitDays} days`;
}

// "Canal Winchester, OH 43110": city and state from the stop or the carrier's lane, plus ZIP.
function getDraftStopLabel(
  stop: Undef<QuoteFormStop>,
  lane: Undef<LTLQuoteLaneAddress>,
  fallback: string
): string {
  const city = toTitleCase((stop?.city || lane?.city || '').trim());
  const state = (stop?.state || lane?.state || '').trim().toUpperCase();
  const zip = (stop?.zip || lane?.zip || '').trim();
  const cityState = [city, state].filter(Boolean).join(', ');
  // Non-breaking space so the ZIP never wraps onto a line by itself.
  const label = [cityState, zip].filter(Boolean).join('\u00a0');

  if (label) {
    return label;
  }

  return stop ? formatLTLDraftLocation(stop, lane ?? {}, fallback) : fallback;
}
